import React, { useEffect, useId, useRef, useState } from 'react';

type Props = {
  label: string;
  emptyLabel: string;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
};

/**
 * Checkable dropdown for Patient Log filters.
 * Empty selection means "all" (same AND semantics as logFilters: a row matches if it is in the set).
 */
export default function MultiCheckDropdown({ label, emptyLabel, options, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  };

  const selected = value;
  const summary =
    selected.length === 0 ? emptyLabel : selected.length > 2 ? `${label} · ${selected.length}` : null;

  return (
    <div className={`mdash-checkdrop${open ? ' mdash-checkdrop--open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`mdash-checkdrop__btn${selected.length ? ' mdash-checkdrop__btn--on' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        disabled={options.length === 0 && selected.length === 0}
        onClick={() => setOpen((v) => !v)}
      >
        {summary != null ? (
          <span className={selected.length === 0 ? 'mdash-checkdrop__placeholder' : undefined}>{summary}</span>
        ) : (
          <span className="mdash-checkdrop__chips">
            {selected.map((name) => (
              <span key={name} className="mdash-checkdrop__chip">
                {name}
              </span>
            ))}
          </span>
        )}
        <span className="mdash-checkdrop__caret" aria-hidden="true" />
      </button>
      {open ? (
        <div className="mdash-checkdrop__menu" id={listId} role="listbox" aria-label={label} aria-multiselectable="true">
          {options.length === 0 ? <p className="mdash-checkdrop__empty">None in this range</p> : null}
          {options.map((name) => {
            const checked = selected.includes(name);
            return (
              <label key={name} className={`mdash-checkdrop__opt${checked ? ' is-on' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(name)} />
                <span>{name}</span>
              </label>
            );
          })}
          {selected.length > 0 ? (
            <button type="button" className="mdash-checkdrop__clear" onClick={() => onChange([])}>
              Clear {label.toLowerCase()}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
